import { useEffect, useMemo, useState } from 'react'

import { IconClock, IconExclamationCircle, IconX } from '@tabler/icons-react'
import { Tooltip } from 'antd'
import { get, set } from 'local-storage'
import moment from 'moment'
import styled from 'styled-components'

import { Role } from '../adminUsers/roles'

import { Button } from '@/components/shared-components/BUI/Button'
import { openLiveChat } from '@/components/util-components/live-chat/LiveChat'
import { useUser } from '@/context/userContext'
import { useHasRoles } from '@/hooks/useHasRoles'

type BannerWrapperProps = {
  visible: boolean
}

const BannerWrapper = styled.div<BannerWrapperProps>`
  display: flex;
  z-index: 1031;

  @media (pointer: coarse) {
    ${({ visible }) => !visible && `display: none;`}
  }

  @media (pointer: fine) {
    position: fixed;
    left: 50%;
    top: 0;
    border-bottom-left-radius: 999px;
    border-bottom-right-radius: 999px;

    ${({ visible }) => visible && `transform: translateX(-50%);`}
    ${({ visible }) =>
      !visible &&
      `
        transform: translateX(-50%) translateY(-80%);
        &:hover {
          transform: translateX(-50%);
        }
      `}
  }

  background-color: ${({ theme }) => theme.colour.primary.base};
  color: ${({ theme }) => theme.colour.secondary.base};
  box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3);
  padding: 0.5rem 2.5rem;
  transition: 0.3s all ease-in-out;
  justify-content: center;
  align-items: center;
  gap: 1rem;
`

const TextWrapper = styled.div`
  ${({ theme }) => theme.font.caption02}
  font-weight: 600;
  text-align: center;
  flex: 1;
  display: flex;
  flex-wrap: nowrap;
  gap: 1rem;
  align-items: center;
`

const LS_TRIAL_BANNER_VISIBLE = 'buildpass-trial-banner-visible'
const LS_TRIAL_BANNER_REMINDER = 'buildpass-trial-banner-reminder'

const TrialBanner: React.FC = () => {
  const trialBannerVisible = get<boolean>(LS_TRIAL_BANNER_VISIBLE)

  const [showBanner, setShowBanner] = useState(trialBannerVisible === null)
  const { builder } = useUser()
  const isSuperAdmin = useHasRoles(Role.SuperAdmin)

  const isExpiringSoon = useMemo(
    () =>
      builder.trialExpiresAt &&
      moment(builder.trialExpiresAt).isAfter(moment()) &&
      moment(builder.trialExpiresAt).isBefore(moment().add(1, 'week')),
    [builder.trialExpiresAt]
  )

  const handleHideBanner = () => {
    setShowBanner(false)
    set(LS_TRIAL_BANNER_VISIBLE, false)

    if (isExpiringSoon) {
      set(LS_TRIAL_BANNER_REMINDER, true)
    }
  }

  useEffect(() => {
    if (
      trialBannerVisible === false &&
      isExpiringSoon &&
      !get<boolean>(LS_TRIAL_BANNER_REMINDER)
    ) {
      setShowBanner(true)
    }
  }, [isExpiringSoon, trialBannerVisible])

  return builder.isTrial && builder.trialExpiresAt && !isSuperAdmin ? (
    <BannerWrapper visible={showBanner}>
      <Tooltip
        title={`${
          builder.trialExpiresAt
            ? `Your trial account will expire on ${moment(
                builder.trialExpiresAt
              ).format('dddd, MMMM Do')}. `
            : ''
        }Please contact the BuildPass team to extend your trial or upgrade`}
      >
        <TextWrapper>
          <IconExclamationCircle size={16} />
          Trial account
        </TextWrapper>
      </Tooltip>

      {isExpiringSoon && (
        <Tooltip
          placement="bottom"
          title={`Click here to chat to our team to extend your trial or upgrade`}
        >
          <Button
            shape="pill"
            size="small"
            icon={<IconClock size={16} />}
            onClick={openLiveChat}
          >
            Extend
          </Button>
        </Tooltip>
      )}

      <Tooltip placement="bottom" title={`Close banner`}>
        <Button
          shape="pill"
          size="small"
          icon={<IconX size={16} />}
          onClick={handleHideBanner}
        >
          Hide
        </Button>
      </Tooltip>
    </BannerWrapper>
  ) : null
}

export default TrialBanner
